import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import type { z } from 'zod';
import { platformImpersonateInput, type PlatformImpersonationTarget } from '@barbeiropro/contracts';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { api, errorMessage } from '@/lib/api';

type Values = z.input<typeof platformImpersonateInput>;

/** "Entrar como": escolhe um membro do tenant (ou já vem pré-selecionado) e, se quiser, informa o motivo (vai para a auditoria). Abre o /app já como o alvo. */
export function ImpersonateModal({ orgId, orgName, initialUserId, onClose }: { orgId: string; orgName: string; initialUserId?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const targets = useQuery({
    queryKey: ['platform', 'impersonation-targets', orgId],
    queryFn: () => api.get<PlatformImpersonationTarget[]>(`/platform/organizations/${orgId}/impersonation-targets`),
  });
  const form = useForm<Values>({ resolver: zodResolver(platformImpersonateInput), defaultValues: { userId: initialUserId ?? '', reason: '' } });
  const userId = form.watch('userId');
  const start = useMutation({
    mutationFn: (v: Values) => api.post(`/platform/organizations/${orgId}/impersonate`, v),
    onSuccess: () => {
      // a sessão agora é a do alvo: descarta todo o cache (me, plataforma) antes de abrir o /app
      qc.clear();
      navigate('/app', { replace: true });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const list = targets.data ?? [];
  return (
    <FormModal
      open
      onOpenChange={(v) => !v && onClose()}
      title="Entrar como"
      description={`Você acessa ${orgName} como o usuário escolhido por 30 minutos. A ação fica registrada na auditoria.`}
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" form="impersonate-form" disabled={start.isPending || !list.length}>
            {start.isPending ? 'Entrando…' : 'Entrar'}
          </Button>
        </>
      }
    >
      <form id="impersonate-form" noValidate className="grid gap-4" onSubmit={form.handleSubmit((v) => start.mutate(v))}>
        <Field id="imp-user" label="Usuário" error={form.formState.errors.userId?.message ? 'Selecione o usuário' : undefined}>
          <Select value={userId || undefined} onValueChange={(v) => form.setValue('userId', v, { shouldValidate: true })}>
            <SelectTrigger id="imp-user" className="w-full" disabled={targets.isLoading || !list.length}>
              <SelectValue placeholder={targets.isLoading ? 'Carregando…' : list.length ? 'Selecione' : 'Nenhum membro ativo'} />
            </SelectTrigger>
            <SelectContent>
              {list.map((t) => (
                <SelectItem key={t.userId} value={t.userId}>
                  {t.name} ({t.email}) · {t.roleName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field id="imp-reason" label="Motivo (opcional)" error={form.formState.errors.reason?.message}>
          <Textarea id="imp-reason" rows={3} maxLength={300} placeholder="Ex.: cliente relatou erro no fechamento do caixa" {...form.register('reason')} />
        </Field>
      </form>
    </FormModal>
  );
}
